import { useEffect, useMemo, useState } from "react";
import { waeAPI, fmtDT } from "./waeApi";
import { EmptyState } from "./uiKit";
import { ScrollText, Search } from "lucide-react";

const ACTION_TONE = {
  created:            "bg-slate-100 text-slate-700",
  level_started:      "bg-sky-100 text-sky-700",
  approved:           "bg-emerald-100 text-emerald-700",
  auto_approved:      "bg-emerald-100 text-emerald-700",
  rejected:           "bg-rose-100 text-rose-700",
  sent_back:          "bg-orange-100 text-orange-700",
  info_requested:     "bg-violet-100 text-violet-700",
  delegate:           "bg-cyan-100 text-cyan-700",
  reassign:           "bg-cyan-100 text-cyan-700",
  escalated:          "bg-amber-100 text-amber-800",
  escalated_sla:      "bg-amber-100 text-amber-800",
  cancelled:          "bg-slate-100 text-slate-500",
  completed:          "bg-emerald-200 text-emerald-800",
  commented:          "bg-slate-100 text-slate-600",
  level_partial_approval: "bg-sky-100 text-sky-700",
  delegation_created: "bg-cyan-100 text-cyan-700",
};

export default function WAEAudit() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [action, setAction] = useState("");

  const load = () => waeAPI.audit({ action: action || undefined, limit: 400 }).then(setRows);
  useEffect(() => { load(); }, [action]);   // eslint-disable-line

  const filtered = useMemo(() =>
    q ? rows.filter(e =>
      (e.request_code||"").toLowerCase().includes(q.toLowerCase()) ||
      (e.actor_email||"").toLowerCase().includes(q.toLowerCase()) ||
      (e.action||"").toLowerCase().includes(q.toLowerCase())
    ) : rows
  , [rows, q]);

  const actionOpts = useMemo(() => Array.from(new Set(rows.map(r => r.action))).sort(), [rows]);

  return (
    <div className="space-y-4" data-testid="wae-audit">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2"/>
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search audit…" aria-label="Search audit"
                 data-testid="wae-audit-search"
                 className="h-10 pl-9 pr-3 rounded-lg border border-slate-200 bg-white text-[13px] w-72 focus:border-hocp-navy outline-none"/>
        </div>
        <select value={action} onChange={e=>setAction(e.target.value)}
                data-testid="wae-audit-action-filter"
                className="h-10 px-3 rounded-lg border border-slate-200 bg-white text-[13px]">
          <option value="">All actions</option>
          {actionOpts.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <div className="ml-auto text-[12px] text-slate-500 mono">{filtered.length} events</div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {filtered.length === 0
          ? <EmptyState icon={ScrollText} title="No audit records"
              msg="Every workflow action creates an immutable audit event."/>
          : (
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">When</th>
                  <th className="text-left px-4 py-3">Request</th>
                  <th className="text-left px-4 py-3">Action</th>
                  <th className="text-left px-4 py-3">Actor</th>
                  <th className="text-left px-4 py-3">Prev → New</th>
                  <th className="text-left px-4 py-3">Reason / Meta</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map(e => (
                  <tr key={e.id} data-testid={`wae-audit-row-${e.id}`} className="hover:bg-slate-50/50">
                    <td className="px-4 py-3 mono text-[11.5px] text-slate-500">{fmtDT(e.at)}</td>
                    <td className="px-4 py-3 mono text-[12px] font-medium text-hocp-navy">{e.request_code || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-md text-[11px] font-medium ${ACTION_TONE[e.action] || "bg-slate-100 text-slate-600"}`}>
                        {(e.action || "").replace(/_/g," ")}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[12px] text-slate-600 mono">{e.actor_email || "—"}</td>
                    <td className="px-4 py-3 text-[11.5px] text-slate-500">
                      {e.previous_status ? <span>{e.previous_status} <span className="text-slate-400">→</span> {e.new_status || "—"}</span> : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-[11.5px] text-slate-600 max-w-md truncate italic">
                      {e.reason || (e.meta && Object.entries(e.meta).slice(0,2).map(([k,v]) => `${k}=${String(v).slice(0,30)}`).join(" · ")) || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>
    </div>
  );
}
